import { ref } from "vue";

import { useProductsStore } from "@/stores/Products";
import { useNotificationAcknowledgement } from "@/composables/notification-acknowledgement";

import type { Category, ContentfulItem, Product } from "@tmi/types/entities";

const storageKey = "tmi-content-read-state-v1";
const unreadWindowMs = 5 * 24 * 60 * 60 * 1000;

type ReadState = Record< string, number >;
export type UnreadCountOptions = {
    categorySlug?: string,
    productSlug?: string,
};

const readStateVersion = ref( 0 );

function canUseLocalStorage(): boolean {
    return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function readState(): ReadState {
    if ( !canUseLocalStorage() ) {
        return {};
    }

    try {
        const raw = window.localStorage.getItem( storageKey );
        if ( !raw ) {
            return {};
        }

        const parsed = JSON.parse( raw ) as ReadState;
        const pruned = pruneState( parsed );
        if ( Object.keys( pruned ).length !== Object.keys( parsed ).length ) {
            window.localStorage.setItem( storageKey, JSON.stringify( pruned ) );
        }

        return pruned;
    } catch {
        return {};
    }
}

function writeState( state: ReadState ): void {
    if ( !canUseLocalStorage() ) {
        return;
    }

    try {
        window.localStorage.setItem( storageKey, JSON.stringify( state ) );
        readStateVersion.value++;
    } catch {
        // Ignore storage failures; unread badges are a progressive UI enhancement.
    }
}

function pruneState( state: ReadState, now = Date.now() ): ReadState {
    const next: ReadState = {};
    Object.entries( state ).forEach( ( [ key, viewedAt ] ) => {
        if ( now - viewedAt <= unreadWindowMs ) {
            next[ key ] = viewedAt;
        }
    } );

    return next;
}

function canonicalContentType( contentType: string | undefined ): string | undefined {
    switch ( contentType ) {
        case 'article':
            return 'articles';
        case 'webinar':
            return 'webinars';
        default:
            return contentType;
    }
}

function legacyContentTypes( contentType: string | undefined ): string[] {
    switch ( contentType ) {
        case 'articles':
            return [ 'article' ];
        case 'webinars':
            return [ 'webinar' ];
        default:
            return [];
    }
}

function contentKey( content: ContentfulItem | undefined, contentType: string | undefined ): string | undefined {
    const id = content?.contentfulId ?? content?.slug;
    const normalizedType = canonicalContentType( contentType );
    if ( !id || !normalizedType ) {
        return undefined;
    }

    return `${ normalizedType }:${ id }`;
}

function readableContentKeys( content: ContentfulItem | undefined, contentType: string | undefined ): string[] {
    const id = content?.contentfulId ?? content?.slug;
    const primaryKey = contentKey( content, contentType );
    if ( !id || !primaryKey ) {
        return [];
    }

    return [
        primaryKey,
        ...legacyContentTypes( canonicalContentType( contentType ) ).map( ( legacyType: string ) => `${ legacyType }:${ id }` )
    ];
}

function isWithinLastWeek( publishedDate: string | Date | undefined, now = Date.now() ): boolean {
    if ( !publishedDate ) {
        return false;
    }

    const publishedAt = new Date( publishedDate ).getTime();
    return Number.isFinite( publishedAt ) && now - publishedAt >= 0 && now - publishedAt <= unreadWindowMs;
}

function contentMatchesProduct( content: ContentfulItem, productSlug?: string ): boolean {
    const products = ( content.products ?? [] ) as Product[];
    if ( !productSlug ) {
        return true;
    }

    const activeProduct = useProductsStore().productBySlug( productSlug );
    return products.some( ( product: Product ) =>
        product.slug === productSlug || ( !!activeProduct && product.id === activeProduct.id )
    );
}

function contentMatchesCategory( content: ContentfulItem, categorySlug?: string ): boolean {
    if ( !categorySlug ) {
        return true;
    }

    const categories = ( content.categories ?? [] ) as Category[];
    return categories.some( ( category: Category ) => category.slug === categorySlug );
}

function normalizeUnreadCountOptions( productSlugOrOptions?: string | UnreadCountOptions ): UnreadCountOptions {
    if ( typeof productSlugOrOptions === 'string' ) {
        return { productSlug: productSlugOrOptions };
    }

    return productSlugOrOptions ?? {};
}

export type UnreadContentEntry = {
    content: ContentfulItem,
    contentType: string,
};

export function useContentReadState() {
    // Hover-to-acknowledge state is treated as "cleared" by every counter
    // sourced from this composable — sidebar badges, ProductWorkspace tabs,
    // top notifications bell. The dropdown LIST (unreadRecentContentList)
    // intentionally does NOT factor it in, so ack'd rows stay visible with a
    // dimmed dot until the user actually clicks them.
    const { isAcknowledged } = useNotificationAcknowledgement();

    function markContentRead( content: ContentfulItem | undefined, contentType: string | undefined ): void {
        const key = contentKey( content, contentType );
        if ( !key ) {
            return;
        }

        const state = pruneState( readState() );
        state[ key ] = Date.now();
        writeState( state );
    }

    function isContentRead( content: ContentfulItem | undefined, contentType: string | undefined ): boolean {
        const keys = readableContentKeys( content, contentType );
        if ( keys.length === 0 ) {
            return false;
        }

        readStateVersion.value;
        const state = pruneState( readState() );
        return keys.some( ( key: string ) => !!state[ key ] );
    }

    function isUnreadRecentContent( content: ContentfulItem | undefined, contentType: string | undefined ): boolean {
        return !!content
            && isWithinLastWeek( content.publishedDate )
            && !isContentRead( content, contentType )
            && !isAcknowledged( content, contentType );
    }

    function unreadCountForContent( contents: ContentfulItem[], contentType: string, productSlugOrOptions?: string | UnreadCountOptions ): number {
        const options = normalizeUnreadCountOptions( productSlugOrOptions );

        readStateVersion.value;
        return contents.reduce( ( count: number, content: ContentfulItem ) => {
            if (
                !isWithinLastWeek( content.publishedDate ) ||
                isContentRead( content, contentType ) ||
                isAcknowledged( content, contentType ) ||
                !contentMatchesProduct( content, options.productSlug ) ||
                !contentMatchesCategory( content, options.categorySlug )
            ) {
                return count;
            }

            return count + 1;
        }, 0 );
    }

    function unreadRecentContentList( contents: ContentfulItem[], contentType: string, productSlugOrOptions?: string | UnreadCountOptions ): ContentfulItem[] {
        const options = normalizeUnreadCountOptions( productSlugOrOptions );

        readStateVersion.value;
        return contents
            .filter( ( content: ContentfulItem ) =>
                isWithinLastWeek( content.publishedDate ) &&
                !isContentRead( content, contentType ) &&
                contentMatchesProduct( content, options.productSlug ) &&
                contentMatchesCategory( content, options.categorySlug )
            )
            .sort( ( a: ContentfulItem, b: ContentfulItem ) =>
                new Date( b.publishedDate ).getTime() - new Date( a.publishedDate ).getTime()
            );
    }

    function markAllContentRead( contents: ContentfulItem[], contentType: string, productSlugOrOptions?: string | UnreadCountOptions ): void {
        const options = normalizeUnreadCountOptions( productSlugOrOptions );
        const state = pruneState( readState() );
        const now = Date.now();
        let changed = false;

        contents.forEach( ( content: ContentfulItem ) => {
            if (
                !isWithinLastWeek( content.publishedDate ) ||
                !contentMatchesProduct( content, options.productSlug ) ||
                !contentMatchesCategory( content, options.categorySlug )
            ) {
                return;
            }

            readableContentKeys( content, contentType ).forEach( ( key: string ) => {
                state[ key ] = now;
                changed = true;
            } );
        } );

        if ( changed ) {
            writeState( state );
        }
    }

    return {
        isUnreadRecentContent,
        markAllContentRead,
        markContentRead,
        unreadCountForContent,
        unreadRecentContentList,
    };
}
